// services/notification.service.js
//
// HTTP client for the notification center (Task 5 — Phase 1b).
// Modeled on src/services/orderBoxItem.service.js — uses jsonApiClient
// for Sanctum SPA cookie + X-XSRF-TOKEN + 419/401 interceptors.
//
// Mid-session 401 from any of these calls fires auth:unauthorized → logout
// via the shared axios response interceptor in httpConfig/config/axios.js.
// DO NOT add a separate 401 path here.
import { jsonApiClient } from '@/httpConfig/config/axios.js';

// --------------------------------------------------------------------------
// Error normalisation (mirrors orderBoxItem.service pattern)
// --------------------------------------------------------------------------

function normalizeError(error) {
  const status = error?.response?.status;
  const detail =
    error?.response?.data?.errors?.[0]?.detail ||
    error?.response?.data?.message ||
    null;
  const normalized = new Error(detail || error?.message || 'Request failed');
  normalized.status = status;
  normalized.detail = detail;
  normalized.original = error;
  return normalized;
}

// --------------------------------------------------------------------------
// Section 3 — Notification list (paginated, JSON:API)
// --------------------------------------------------------------------------

/**
 * Fetch the notification list for a user.
 *
 * @param {string|number} userId
 * @param {object} params
 * @param {number}  [params.page]     page[number] — 1-based
 * @param {number}  [params.size]     page[size] — max 50
 * @param {string}  [params.filter]   'unread' | 'read' | undefined (all)
 * @param {string}  [params.sort]     '-createdAt' (default) | 'createdAt'
 * @returns {Promise<{ items: object[], meta: object, links: object }>}
 */
export async function fetchNotifications(userId, { page = 1, size = 20, filter, sort = '-createdAt' } = {}) {
  try {
    const queryParams = {
      'page[number]': page,
      'page[size]': size,
      sort,
    };
    if (filter) queryParams['filter[read]'] = filter;

    const response = await jsonApiClient.get(
      `/users/${userId}/-actions/notifications`,
      { params: queryParams },
    );

    // jsonApiClient runs jsona deserialization: response.data.data is deserialized,
    // response.data.meta and response.data.links carry pagination info.
    return {
      items: response.data?.data ?? [],
      meta: response.data?.meta ?? {},
      links: response.data?.links ?? {},
    };
  } catch (error) {
    throw normalizeError(error);
  }
}

// --------------------------------------------------------------------------
// Section 4 — Unread count
// --------------------------------------------------------------------------

/**
 * Fetch the current unread notification count for a user.
 *
 * @param {string|number} userId
 * @returns {Promise<number>}
 */
export async function fetchUnreadCount(userId) {
  try {
    const response = await jsonApiClient.get(
      `/users/${userId}/-actions/notifications/-actions/unread-count`,
    );
    return response.data?.data?.unreadCount ?? 0;
  } catch (error) {
    throw normalizeError(error);
  }
}

// --------------------------------------------------------------------------
// Section 5 — Mark read / read-all
// --------------------------------------------------------------------------

/**
 * Mark a single notification as read.
 * Sets readAt + openedAt on the server. Idempotent (re-calling is still 204).
 *
 * @param {string} notificationId  UUID of the notification
 * @returns {Promise<void>}
 */
export async function markNotificationRead(notificationId) {
  try {
    await jsonApiClient.post(`/notifications/${notificationId}/-actions/read`);
  } catch (error) {
    throw normalizeError(error);
  }
}

/**
 * Mark ALL notifications for a user as read.
 * Returns 204. Idempotent.
 *
 * @param {string|number} userId
 * @returns {Promise<void>}
 */
export async function markAllNotificationsRead(userId) {
  try {
    await jsonApiClient.post(
      `/users/${userId}/-actions/notifications/-actions/read-all`,
    );
  } catch (error) {
    throw normalizeError(error);
  }
}

// --------------------------------------------------------------------------
// Section 6 — Notification preferences
// --------------------------------------------------------------------------

/**
 * Fetch notification preferences for a user.
 *
 * Note: `quietHours` is a BACKEND-PASS-THROUGH field as of 2026-06-05.
 * The frontend no longer surfaces quiet-hours UI (user directive killed
 * the MVP feature, including the prayer/Ramadan-aware enhancement G20).
 * Existing server-side `quietHours` values are still returned here and
 * held in `prefs.value.quietHours` on the preferences page to prevent
 * type-toggle PATCHes from clobbering them — but no UI displays or edits
 * the field. If/when backend drops the field, this degrades to `null`.
 *
 * @param {string|number} userId
 * @returns {Promise<{ channels: object, types: object, quietHours: object|null }>}
 */
export async function fetchNotificationPreferences(userId) {
  try {
    const response = await jsonApiClient.get(
      `/users/${userId}/-actions/notification-preferences`,
    );
    const data = response.data?.data ?? {};
    return {
      channels: data.channels ?? { push: true, telegram: true },
      types: data.types ?? {},
      quietHours: data.quietHours ?? null,
    };
  } catch (error) {
    throw normalizeError(error);
  }
}

/**
 * PATCH notification preferences for a user.
 * Accepts a partial document — omitted keys are preserved on the server.
 *
 * Note: as of 2026-06-05 the frontend no longer SENDS `quietHours` in
 * any patch (the UI was removed per user directive); the server side
 * still accepts it for backward compatibility with legacy clients. The
 * response shape continues to include `quietHours` as pass-through —
 * see `fetchNotificationPreferences` for the rationale.
 *
 * @param {string|number} userId
 * @param {object} patch  Partial preferences object. e.g.:
 *   { types: { 'orderbox.shipped.invoice_ready': { push: false } } }
 * @returns {Promise<{ channels: object, types: object, quietHours: object|null }>}
 */
export async function updateNotificationPreferences(userId, patch) {
  try {
    const response = await jsonApiClient.patch(
      `/users/${userId}/-actions/notification-preferences`,
      { data: patch },
    );
    const data = response.data?.data ?? {};
    return {
      channels: data.channels ?? { push: true, telegram: true },
      types: data.types ?? {},
      quietHours: data.quietHours ?? null,
    };
  } catch (error) {
    throw normalizeError(error);
  }
}
